import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { Plus, Edit, Trash2, ArrowUp, ArrowDown, X, Upload } from 'lucide-react';
import RichTextEditor from './RichTextEditor';
import api from '../../utils/api';

const NavigationManagement = ({ onUpdate }) => {
  const [navigation, setNavigation] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingNav, setEditingNav] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    label: '',
    path: '',
    order: 0,
    content: '',
    parent_id: null,  // 親メニューID
    image_url: '',    // 挨拶者の写真URL
  });
  const { toast } = useToast();

  const fetchNavigation = React.useCallback(async () => {
    try {
      const response = await api.get('/navigation');
      setNavigation(response.data);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('ナビゲーションの取得に失敗しました:', error);
    }
  }, [onUpdate]);

  useEffect(() => {
    fetchNavigation();
  }, [fetchNavigation]);

  // 画像アップロード関数
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // ファイルサイズチェック (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'エラー',
        description: 'ファイルサイズは5MB以下にしてください',
        variant: 'destructive',
      });
      return;
    }

    // ファイル形式チェック
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: 'エラー',
        description: '画像ファイル（JPG、PNG、GIF、WebP）のみアップロード可能です',
        variant: 'destructive',
      });
      return;
    }

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await api.post('/upload/image', formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setFormData({ ...formData, image_url: response.data.url });
      toast({
        title: '成功',
        description: '画像をアップロードしました',
      });
    } catch (error) {
      console.error('画像のアップロードに失敗しました:', error);
      toast({
        title: 'エラー',
        description: '画像のアップロードに失敗しました',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const openDialog = (nav = null) => {
    if (!isEditMode) {
      toast({
        title: '編集モードを有効にしてください',
        description: '編集を行うには、編集モードボタンをクリックしてください。',
        variant: 'destructive',
      });
      return;
    }

    if (nav) {
      setEditingNav(nav);
      setFormData({
        label: nav.label,
        path: nav.path,
        order: nav.order,
        content: nav.content || '',
        parent_id: nav.parent_id || null,
        image_url: nav.image_url || '',
      });
    } else {
      setEditingNav(null);
      setFormData({
        label: '',
        path: '',
        order: navigation.length,
        content: '',
        parent_id: null,
        image_url: '',
      });
    }
    setIsDialogOpen(true);
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (editingNav) {
        await api.put(`/navigation/${editingNav.id}`, formData);
        toast({
          title: '更新完了',
          description: 'メニューを更新しました',
        });
      } else {
        await api.post('/navigation', formData);
        toast({
          title: '作成完了',
          description: '新しいメニューを作成しました',
        });
      }
      
      setIsDialogOpen(false);
      resetForm();
      fetchNavigation();
    } catch (error) {
      toast({
        title: 'エラー',
        description: error.response?.data?.detail || '操作に失敗しました',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingNav(item);
    setFormData({
      label: item.label,
      path: item.path,
      order: item.order,
      content: item.content || '',
      parent_id: item.parent_id || null,
      image_url: item.image_url || '',
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('本当に削除しますか？')) {
      return;
    }

    try {
      await api.delete(`/navigation/${id}`);
      toast({
        title: '削除完了',
        description: 'メニューを削除しました',
      });
      fetchNavigation();
    } catch (error) {
      toast({
        title: 'エラー',
        description: error.response?.data?.detail || '削除に失敗しました',
        variant: 'destructive',
      });
    }
  };

  const resetForm = () => {
    setFormData({
      label: '',
      path: '',
      order: navigation.length,
      content: '',
      parent_id: null,
      image_url: '',
    });
    setEditingNav(null);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    resetForm();
  };

  return (
    <Card className="bg-gradient-to-br from-gray-50/80 to-gray-100/80 border-amber-500/30 backdrop-blur-sm">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <CardTitle className="text-xl sm:text-2xl bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">
            ナビゲーションメニュー管理
          </CardTitle>
          <div className="flex gap-2 flex-wrap">
            {!isEditMode && (
              <Button 
                onClick={() => setIsEditMode(true)}
                variant="outline"
                className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10"
              >
                <Edit className="w-4 h-4 mr-2" />
                編集モード
              </Button>
            )}
            {isEditMode && (
              <Button 
                onClick={() => setIsEditMode(false)}
                variant="outline"
                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
              >
                <X className="w-4 h-4 mr-2" />
                編集終了
              </Button>
            )}
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                onClick={resetForm}
                className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 shadow-lg shadow-amber-500/30"
              >
                <Plus className="w-4 h-4 mr-2" />
                新規作成
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-gray-50 to-gray-100 border-amber-500/30 text-gray-900">
              <DialogHeader>
                <DialogTitle className="text-2xl bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">
                  {editingNav ? 'メニューを編集' : '新しいメニューを作成'}
                </DialogTitle>
                <DialogDescription className="text-gray-600">
                  {editingNav ? 'メニューの内容を編集します' : '新しいメニューを作成します'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="label" className="text-gray-600">ラベル</Label>
                  <Input
                    id="label"
                    value={formData.label}
                    onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                    placeholder="例: 企業理念"
                    className="bg-white/50 border-amber-500/30 text-gray-900 focus:border-amber-500"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="parent" className="text-gray-600">親メニュー（サブメニューとして追加する場合）</Label>
                  <select
                    id="parent"
                    value={formData.parent_id || ''}
                    onChange={(e) => setFormData({ ...formData, parent_id: e.target.value || null })}
                    className="flex h-10 w-full rounded-md border border-amber-500/30 bg-white/50 px-3 py-2 text-sm text-gray-900 ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="">なし（トップレベルメニュー）</option>
                    {navigation
                      .filter(nav => !nav.parent_id && nav.id !== editingNav?.id)
                      .map(nav => (
                        <option key={nav.id} value={nav.id}>
                          {nav.label}
                        </option>
                      ))}
                  </select>
                  <p className="text-xs text-gray-500">
                    親メニューを選択すると、ドロップダウンメニューのサブアイテムとして表示されます
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="path" className="text-gray-600">パス</Label>
                  <Input
                    id="path"
                    value={formData.path}
                    onChange={(e) => setFormData({ ...formData, path: e.target.value })}
                    placeholder="例: /philosophy"
                    className="bg-white/50 border-amber-500/30 text-gray-900 focus:border-amber-500"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="image_url" className="text-gray-600">画像URL（任意 - サブメニュー用）</Label>
                  <div className="flex gap-2">
                    <Input
                      id="image_url"
                      value={formData.image_url}
                      onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                      placeholder="例: https://example.com/image.jpg"
                      className="bg-white/50 border-amber-500/30 text-gray-900 focus:border-amber-500 flex-1"
                    />
                    <div className="relative">
                      <input
                        type="file"
                        id="image-upload"
                        accept="image/*"
                        className="hidden"
                        onChange={handleImageUpload}
                        disabled={uploading}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => document.getElementById('image-upload').click()}
                        disabled={uploading}
                        className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10"
                      >
                        {uploading ? (
                          <span className="flex items-center gap-2">
                            <span className="animate-spin">⏳</span>
                            アップロード中...
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            <Upload className="w-4 h-4" />
                            アップロード
                          </span>
                        )}
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">
                    挨拶者の写真URLを入力するか、画像をアップロードしてください。サブメニューに表示されます。
                  </p>
                  {formData.image_url && (
                    <div className="mt-2 flex items-center gap-2">
                      <img 
                        src={formData.image_url} 
                        alt="プレビュー" 
                        className="w-16 h-16 rounded-full object-cover border-2 border-amber-500/30"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => setFormData({ ...formData, image_url: '' })}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="order" className="text-gray-600">表示順序</Label>
                  <Input
                    id="order"
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                    className="bg-white/50 border-amber-500/30 text-gray-900 focus:border-amber-500"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="content" className="text-gray-600">ページ内容</Label>
                  <RichTextEditor
                    value={/<[a-z][\s\S]*>/i.test(formData.content || '') ? formData.content : (formData.content || '').replace(/\n/g, '<br>')}
                    onChange={(html) => setFormData({ ...formData, content: html })}
                    placeholder="本文をここに入力してください"
                    testId="nav-content-editor"
                  />
                  <p className="text-xs text-gray-500">
                    太字・文字サイズ・色・箇条書き・配置などをツールバーで設定できます（Wordのように編集できます）
                  </p>
                </div>
                <div className="flex justify-end space-x-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={handleDialogClose}
                    className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10"
                  >
                    キャンセル
                  </Button>
                  <Button 
                    type="submit"
                    className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700"
                    disabled={loading}
                  >
                    {loading ? '処理中...' : (editingNav ? '更新' : '作成')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {navigation
            .filter(item => !item.parent_id)
            .map((item) => (
            <div key={item.id} className="space-y-2">
              <div
                className="border border-amber-500/20 rounded-lg p-4 hover:border-amber-500/50 bg-white/30 transition-all backdrop-blur-sm"
              >
                <div className="flex justify-between items-center">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <span className="text-sm text-gray-500">順序: {item.order}</span>
                      <h3 className="text-lg font-semibold text-gray-900">{item.label}</h3>
                      <span className="text-sm text-amber-500">{item.path}</span>
                    </div>
                  </div>
                  <div className="flex space-x-2 ml-4">
                    {isEditMode && (
                      <>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleEdit(item)}
                          className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleDelete(item.id)}
                          className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
              
              {/* サブメニュー表示 */}
              {navigation
                .filter(sub => sub.parent_id === item.id)
                .map((subItem) => (
                  <div
                    key={subItem.id}
                    className="ml-8 border border-amber-500/20 rounded-lg p-3 hover:border-amber-500/50 bg-white/20 transition-all backdrop-blur-sm"
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <span className="text-xs text-gray-600">└─</span>
                          <span className="text-sm text-gray-500">順序: {subItem.order}</span>
                          <h4 className="text-md font-medium text-gray-600">{subItem.label}</h4>
                          <span className="text-sm text-amber-500">{subItem.path}</span>
                        </div>
                      </div>
                      <div className="flex space-x-2 ml-4">
                        {isEditMode && (
                          <>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => handleEdit(subItem)}
                              className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => handleDelete(subItem.id)}
                              className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default NavigationManagement;
